import { FormField, FormSection, HelpTip, Toggle, ToggleGroup } from '../common'
import PromotionSettings from './PromotionSettings'
import {
  isPostComposerCopyCountEligible,
  resolvePostComposerCopyCountEnabled,
} from './postComposerModel'
import styles from './PostComposer.module.css'

export default function AdvancedSettingsSection({
  values,
  isEditMode = false,
  expanded,
  onToggle,
  onChange,
  onToggleChange,
  showPromotion = true,
  showEngagement = true,
  showVisibility = true,
  showVisibilityToggles = true,
  // Algorithm boost and system score are Wager ranking internals; book users
  // order their feed with Manual order and pins instead.
  showRanking = true,
  ctaChildren = null,
}) {
  const handleNumberChange = (field) => (event) => {
    const value = parseFloat(event.target.value)
    onChange(field, Number.isNaN(value) ? 0 : value)
  }
  const copyCountEligible = isPostComposerCopyCountEligible(values)
  const copyCountEnabled = resolvePostComposerCopyCountEnabled(values)

  return (
    <FormSection
      headingSize="subsection"
      title="Advanced settings"
      collapsible
      expanded={expanded}
      onToggle={onToggle}
      className={styles.advancedSection}
      contentClassName={styles.advancedSectionContent}
    >
      {showPromotion || ctaChildren ? (
        <div className={styles.advancedPrimaryGrid}>
          {showPromotion ? (
            <PromotionSettings
              postType={values.postType}
              expiresAt={values.expiresAt}
              scheduledFor={values.scheduledFor}
              promoPriority={values.promoPriority}
              interleaveEvery={values.interleaveEvery}
              placementMode={values.placementMode}
              onChange={onChange}
            />
          ) : null}
          {ctaChildren}
        </div>
      ) : null}

      {showEngagement || (showVisibility && showVisibilityToggles) ? (
        <section className={styles.advancedRegion} aria-labelledby="advanced-visibility-heading">
          <h4 id="advanced-visibility-heading" className={styles.advancedGroupTitle}>Visibility and actions</h4>
          <div className={styles.advancedToggleGrid}>
            {showEngagement ? (
              <div className={styles.advancedControlGroup}>
                <ToggleGroup label="Audience actions">
                  <Toggle
                    label="Share link"
                    labelExtra={(
                      <HelpTip
                        text="Adds a share action that copies a direct link to this post."
                        label="Share link help"
                      />
                    )}
                    checked={values.shareEnabled !== false}
                    onChange={(value) => onToggleChange('shareEnabled', value)}
                  />
                  <Toggle
                    label="Copy activity"
                    labelExtra={(
                      <HelpTip
                        text={copyCountEligible
                          ? 'Shows seeded social proof plus actual copy-bet users. Studio analytics report actual users only.'
                          : 'Available when the post has bet legs and an add-to-betslip action.'}
                        label="Copy activity help"
                      />
                    )}
                    checked={copyCountEnabled}
                    disabled={!copyCountEligible}
                    onChange={(value) => onToggleChange('copyCountEnabled', value)}
                  />
                </ToggleGroup>
              </div>
            ) : null}

            {showVisibility && showVisibilityToggles ? (
              <div className={styles.advancedControlGroup}>
                <ToggleGroup label="Feed status">
                  <Toggle
                    label="Hidden"
                    checked={Boolean(values.isHidden)}
                    onChange={(value) => onToggleChange('isHidden', value)}
                  />
                  <Toggle
                    label="Pinned"
                    checked={Boolean(values.isPinned)}
                    onChange={(value) => onToggleChange('isPinned', value)}
                  />
                  <Toggle
                    label="Archive after event ends"
                    labelExtra={(
                      <HelpTip
                        text="Keeps this post in Library under Archived posts for reuse once its event completes. Posts without this are deleted after the event ends."
                        label="Archive after event ends help"
                      />
                    )}
                    checked={Boolean(values.archiveOnCompletion)}
                    onChange={(value) => onToggleChange('archiveOnCompletion', value)}
                  />
                </ToggleGroup>
                {values.isPinned ? (
                  <FormField
                    label="Pinned until"
                    name="pinnedUntil"
                    type="datetime-local"
                    value={values.pinnedUntil}
                    onChange={(event) => onChange('pinnedUntil', event.target.value)}
                    help="Optional end time for the pin."
                    className={styles.advancedCompactField}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {showVisibility && showRanking ? (
        <section className={styles.advancedRegion} aria-labelledby="advanced-ranking-heading">
          <h4 id="advanced-ranking-heading" className={styles.advancedGroupTitle}>Ranking</h4>
          <div className={styles.advancedRankingGrid}>
            <FormField
              label="Algorithm boost"
              name="boostFactor"
              type="number"
              value={values.boostFactor}
              onChange={handleNumberChange('boostFactor')}
              step="0.1"
              min="0"
              help="Multiplies the system score in Automatic order. It does not change a saved Manual order."
              className={styles.priorityField}
            />
            <div className={styles.readOnlyStat}>
              <span className={styles.readOnlyLabel}>System score</span>
              <strong className={styles.readOnlyValue}>
                {isEditMode ? Number(values.score || 0).toFixed(1) : 'Calculated after processing'}
              </strong>
            </div>
          </div>
        </section>
      ) : null}
      {/* The post-level card appearance editor is withheld until it is ready;
          a saved card_presentation passes through edits untouched. */}
    </FormSection>
  )
}
